import { CHART_START_PATTERNS } from '../../../parse-response-content';

/**
 * Result of incomplete block detection.
 */
export type IncompleteBlockInfo = {
  hasIncomplete: boolean;
  safeText: string;
};

/**
 * Find the start index of an unclosed thinking tag.
 * Returns the index where the incomplete block starts, or null if all complete.
 */
const findUnclosedThinkingStart = (text: string): number | null => {
  const openTag = '<thinking>';
  const closeTag = '</thinking>';

  const lastOpenTag = text.lastIndexOf(openTag);
  if (lastOpenTag === -1) {
    return null;
  }

  const afterLastOpen = text.substring(lastOpenTag);
  if (!afterLastOpen.includes(closeTag)) {
    return lastOpenTag;
  }

  return null;
};

/**
 * Find the earliest chart start position in text.
 * Uses shared CHART_START_PATTERNS to stay in sync with the parser.
 */
const findChartStartPosition = (text: string): number => {
  const legacyStart = text.search(CHART_START_PATTERNS.legacy);
  const barStart = text.search(CHART_START_PATTERNS.bar);
  const lineStart = text.search(CHART_START_PATTERNS.line);
  const pieStart = text.search(CHART_START_PATTERNS.pie);

  const positions = [legacyStart, barStart, lineStart, pieStart].filter((p) => p !== -1);

  if (positions.length === 0) {
    return -1;
  }

  return Math.min(...positions);
};

const JSON_CODE_BLOCK_OPEN_LENGTH = '```json'.length;

/**
 * Find the start index of an unclosed markdown JSON code block.
 * Returns the index where the incomplete code block starts, or null if all complete.
 */
const findUnclosedJsonCodeBlock = (text: string): number | null => {
  const openPattern = /```json/g;
  const closePattern = /```/g;

  let lastUnclosedStart: number | null = null;
  let openMatch;

  while ((openMatch = openPattern.exec(text)) !== null) {
    const openStart = openMatch.index;
    const afterOpen = text.substring(openStart + JSON_CODE_BLOCK_OPEN_LENGTH);

    // Find the closing ``` that is NOT part of another opening
    closePattern.lastIndex = 0;
    let closeMatch;
    let foundClose = false;

    while ((closeMatch = closePattern.exec(afterOpen)) !== null) {
      const closePos = closeMatch.index;
      // Check if this is a closing ``` (not opening another code block)
      const afterClose = afterOpen.substring(closePos + 3);
      // If the next non-whitespace is not a language identifier, it's a closing tag
      if (!/^\w+\s*\n/.test(afterClose)) {
        foundClose = true;
        break;
      }
    }

    if (!foundClose) {
      lastUnclosedStart = openStart;
    }
  }

  return lastUnclosedStart;
};

/**
 * Find the start index of an unbalanced chart JSON.
 * Returns the index where the incomplete JSON starts, or null if all complete.
 */
const findUnbalancedChartStart = (text: string): number | null => {
  const chartStart = findChartStartPosition(text);
  if (chartStart === -1) {
    return null;
  }

  // Check if the JSON is balanced
  let braceCount = 0;
  let inString = false;
  let escape = false;
  let foundStart = false;

  for (let i = chartStart; i < text.length; i++) {
    const char = text[i];

    if (escape) {
      escape = false;
      continue;
    }

    if (char === '\\' && inString) {
      escape = true;
      continue;
    }

    if (char === '"') {
      inString = !inString;
      continue;
    }

    if (!inString) {
      if (char === '{') {
        braceCount++;
        foundStart = true;
      }
      if (char === '}') {
        braceCount--;
      }

      if (foundStart && braceCount === 0) {
        // JSON is balanced, check for more chart JSON after this one
        const remainingText = text.substring(i + 1);
        const nextChartStart = findChartStartPosition(remainingText);
        if (nextChartStart !== -1) {
          const absoluteNextStart = i + 1 + nextChartStart;
          const nextResult = findUnbalancedChartStart(text.substring(absoluteNextStart));
          if (nextResult !== null) {
            return absoluteNextStart + nextResult;
          }
        }
        return null;
      }
    }
  }

  // If we get here, braces are unbalanced
  return chartStart;
};

/**
 * Detect incomplete blocks that should be held back during streaming.
 * Returns info about incomplete blocks and the safe text to render.
 *
 * This is streaming-specific logic that determines what content is safe
 * to display while more chunks are arriving.
 */
export const detectIncompleteBlocks = (text: string): IncompleteBlockInfo => {
  const unclosedThinkingStart = findUnclosedThinkingStart(text);
  const unbalancedChartStart = findUnbalancedChartStart(text);
  const unclosedJsonCodeBlock = findUnclosedJsonCodeBlock(text);

  // Find the earliest incomplete block
  const incompletePositions = [
    unclosedThinkingStart,
    unbalancedChartStart,
    unclosedJsonCodeBlock,
  ].filter((pos): pos is number => pos !== null);

  if (incompletePositions.length === 0) {
    return {
      hasIncomplete: false,
      safeText: text,
    };
  }

  const incompleteStart = Math.min(...incompletePositions);

  return {
    hasIncomplete: true,
    safeText: text.substring(0, incompleteStart),
  };
};
